import { useEffect, useRef, useState } from 'react'
import ReactSignature, { SignatureRef } from '@uiw/react-signature'
import { Points, SignatureData } from '@playbooks/action-cards-utils/src'
import { Button } from '@playbooks/ui/components/button'
import { cn } from '@playbooks/ui/lib/utils'

import { useTranslation } from '../../../contexts/localization/Localization'

interface SignatureProps {
  onChange: (value: SignatureData) => void
  value?: SignatureData
}

export default function Signature({ onChange, value }: SignatureProps) {
  const { text } = useTranslation()

  const signatureRef = useRef<SignatureRef>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const tidRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [points, setPoints] = useState<Points[]>([])

  const isSignatureHasPoints = !!value?.points.length

  const handlePoints = (data: Points) => {
    if (data.length === 0 || isSignatureHasPoints) return

    setPoints([...(points || []), data])

    if (tidRef.current) {
      clearTimeout(tidRef.current)
    }

    // Set timeout to wait for the user to finish signing before calling onChange
    tidRef.current = setTimeout(() => {
      if (!containerRef.current) return

      const { clientWidth, clientHeight } = containerRef.current
      const viewBox = `0 0 ${clientWidth} ${clientHeight}`

      signatureRef.current?.svg?.setAttribute('viewBox', viewBox)

      onChange({
        viewBox,
        points: [...(points || []), data],
      })
    }, 1000)
  }

  const clear = () => {
    const container = containerRef.current
    if (!container) return

    const { clientWidth, clientHeight } = container
    signatureRef.current?.clear()
    onChange({ viewBox: `0 0 ${clientWidth} ${clientHeight}`, points: [] })
    setPoints([])
  }

  // Apply points from question value
  useEffect(() => {
    if (!value?.points) return
    // Clear before adding new points
    signatureRef.current?.clear()

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - the type definition for ReactSignature does not allow passing the points as Array of Arrays
    // But to avoid converting the array to an object, we can pass it like an array.
    signatureRef.current?.dispatch(value?.points)
    signatureRef.current?.svg?.setAttribute('viewBox', value?.viewBox)
    // JSON.stringify is preferred to avoid unnecessary re-rendering
    // when the points array is the same but the reference is different
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(value?.points)])

  useEffect(() => {
    // Container Resize observer to handle signature scaling
    const container = containerRef.current
    const signature = signatureRef.current

    if (!container || !signature) return

    const resizeObserver = new ResizeObserver(() => {
      // get clientHeight and clientWidth of the container
      const { clientHeight, clientWidth } = container
      const noPoints = !isSignatureHasPoints

      // if there are no points set viewBox to the size of the container
      // to make sure signature is scaled to the container
      if (noPoints) {
        signature.svg!.setAttribute('viewBox', `0 0 ${clientWidth} ${clientHeight}`)
      }
    })

    resizeObserver.observe(container)
    return () => {
      resizeObserver.disconnect()
    }
  }, [isSignatureHasPoints])

  // Reset timeout on mouse down or touch events to prevent onChange call while user is signing
  const onInteract = () => {
    if (tidRef.current) {
      clearTimeout(tidRef.current)
    }
  }

  return (
    <div
      data-testid="signature-question"
      className="relative border rounded-md overflow-hidden aspect-video shadow"
      ref={containerRef}
    >
      <ReactSignature
        ref={signatureRef}
        onPointer={handlePoints}
        onMouseDown={onInteract}
        onTouchMove={onInteract}
        onTouchStart={onInteract}
        className={cn('bg-background! [&_path]:dark:fill-white', isSignatureHasPoints && 'pointer-events-none')}
        width="100%"
        height="100%"
      />

      {!!isSignatureHasPoints && (
        <div className="absolute right-2 top-2 flex gap-2">
          <Button onClick={clear}>{text('clear')}</Button>
        </div>
      )}
    </div>
  )
}
